import type React from 'react';

import type { WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import st from './ColorPicker.module.sass';
import { useColorPickerTranslation } from './i18n';
import { buildTestid } from '../../lib/utils';

const DEFAULT_COLOR_OPTIONS = [
  { key: 'black', colorVariable: 'var(--Color-Surface-On-Surface, #000D24)' },
  { key: 'positive', colorVariable: 'var(--Color-Semantic-Positive-Positive, var(--color-positive-variant))' },
  { key: 'negative', colorVariable: 'var(--Color-Semantic-Negative-Negative, var(--color-negative-variant))' },
  { key: 'blue', colorVariable: 'var(--Color-Primary-Primary, #005BFF)' },
] as const;

export type DetectionColor = (typeof DEFAULT_COLOR_OPTIONS)[number]['key'];

interface ColorOption {
  key: DetectionColor;
  colorVariable?: string;
}

interface ColorPickerProps extends WithDataTestid {
  value: DetectionColor;
  onChange: (color: DetectionColor) => void;
  colors?: readonly ColorOption[];
  disabled?: boolean;
}

const displayName = 'ColorPicker';
const testid = buildTestid(displayName);

function ColorPicker({
  value,
  onChange,
  disabled = false,
  colors = DEFAULT_COLOR_OPTIONS,
  'data-testid': dataTestId = undefined,
}: ColorPickerProps): React.JSX.Element {
  const { t } = useColorPickerTranslation();

  return (
    <div
      className={st.ColorPicker}
      {...testid(dataTestId)}
    >
      <span className={st.Label}>{t('color')}</span>

      {colors.map(({ key, colorVariable }) => (
        <div
          className={cn(st.ColorWrapper, {
            [st.ColorWrapper_selected]: key === value,
            [st.ColorWrapper_disabled]: disabled,
          })}
          key={key}
        >
          <button
            aria-label={`Select ${key} color`}
            className={cn(st.Color, {
              [st.Color_disabled]: disabled,
            })}
            disabled={disabled}
            onClick={() => onChange(key)}
            style={{ backgroundColor: colorVariable }}
            type="button"
            {...testid('SelectButton', key, dataTestId)}
          />
        </div>
      ))}
    </div>
  );
}

export { ColorPicker };
